import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Switch, message, Upload, Tooltip, Modal } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faImage, faCopy, faUpload } from "@fortawesome/free-solid-svg-icons";
import landingPageApi from "../../../api/landingPageApi";
import { AudienceSelect } from "../../../components/audience-fields";
import { landingPageUrl, toSlug } from "./landing-page-url";
import LandingPagePreview from "./LandingPagePreview";
import LandingPageImages from "./LandingPageImages";
import LandingPageDesignRules from "./LandingPageDesignRules";

const API_PUBLIC_URL = (process.env.REACT_APP_API_PUBLIC_URL || process.env.REACT_APP_API_URL || "")
    .replace("/api/v1", "")
    .replace(/\/$/, "");

/**
 * Tạo/sửa 1 Trang HTML: dán nguyên file HTML ở ô bên trái, xem thử ngay bên phải.
 * Ảnh trong trang nên upload qua nút "Tải ảnh" rồi dán URL vào thẻ <img> —
 * nhẹ hơn nhiều so với nhúng base64 vào HTML.
 */
function LandingPageForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [description, setDescription] = useState("");
    const [html, setHtml] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [audience, setAudience] = useState("all");
    const [audienceDays, setAudienceDays] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    // Slug tự sinh theo tiêu đề cho tới khi admin tự gõ slug (lúc sửa thì không đụng).
    const slugTouched = useRef(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await landingPageApi.getOne(id);
                const page = data.data;
                setTitle(page.title || "");
                setSlug(page.slug || "");
                setDescription(page.description || "");
                setHtml(page.html || "");
                setIsActive(!!page.isActive);
                setAudience(page.audience || "all");
                setAudienceDays(page.audienceDays ?? null);
            } catch {
                message.error("Không tải được nội dung trang");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit]);

    const onTitleChange = (value) => {
        setTitle(value);
        if (!slugTouched.current) setSlug(toSlug(value));
    };

    const onSave = async () => {
        if (!title.trim()) return message.warning("Nhập tiêu đề trang");
        if (!slug.trim()) return message.warning("Nhập đường dẫn (slug)");
        if (!html.trim()) return message.warning("Dán nội dung HTML");

        const payload = {
            title: title.trim(),
            slug: slug.trim(),
            description,
            html,
            isActive,
            audience,
            // Số ngày chỉ có nghĩa với "user mới" — loại khác gửi rỗng để backend xoá giá trị cũ
            audienceDays: audience === "new_user" ? audienceDays : null,
        };
        setSaving(true);
        try {
            if (isEdit) {
                await landingPageApi.update(id, payload);
                message.success("Đã lưu trang");
            } else {
                await landingPageApi.create(payload);
                message.success("Đã tạo trang");
                navigate("/page");
            }
        } catch (error) {
            message.error(error?.response?.data?.message || "Lưu trang thất bại");
        } finally {
            setSaving(false);
        }
    };

    const onUploadImage = async ({ file, onSuccess, onError }) => {
        const formData = new FormData();
        formData.append("image", file);
        try {
            const { data } = await landingPageApi.uploadImage(formData);
            const url = `${API_PUBLIC_URL}${data.data.path}`;
            try {
                await navigator.clipboard.writeText(url);
                message.success("Đã tải ảnh & copy URL — dán vào thẻ <img src=...>");
            } catch {
                message.success(`Đã tải ảnh: ${url}`);
            }
            onSuccess?.(data);
        } catch (error) {
            message.error(error?.response?.data?.message || "Tải ảnh thất bại");
            onError?.(error);
        }
    };

    /**
     * Nạp file .html có sẵn vào ô nội dung. Đọc THẲNG ở trình duyệt (FileReader),
     * không upload lên server: HTML vẫn nằm ở cột `html` như khi dán tay, nên
     * không phải thêm endpoint, không sinh file rác trên đĩa.
     */
    const readHtmlFile = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            setHtml(String(reader.result || ""));
            // File thường đặt tên đúng nội dung — đỡ admin gõ lại khi tạo trang mới.
            if (!title.trim()) onTitleChange(file.name.replace(/\.html?$/i, ""));
            message.success(`Đã nạp ${file.name}`);
        };
        reader.onerror = () => message.error("Không đọc được file");
        reader.readAsText(file, "utf-8");
    };

    const onPickHtmlFile = (file) => {
        // Body /api/v1/page giới hạn 5MB, chừa chỗ cho tiêu đề/mô tả và phần escape JSON.
        const MAX_MB = 4;
        if (file.size > MAX_MB * 1024 * 1024) {
            message.error(`File nặng ${(file.size / 1024 / 1024).toFixed(1)}MB, tối đa ${MAX_MB}MB`);
            return Upload.LIST_IGNORE;
        }
        // Đang có nội dung mà nạp đè là mất trắng phần đã dán → hỏi trước.
        if (html.trim()) {
            Modal.confirm({
                title: "Thay toàn bộ nội dung HTML?",
                content: `Nội dung đang có sẽ bị ghi đè bằng file "${file.name}".`,
                okText: "Ghi đè",
                cancelText: "Huỷ",
                onOk: () => readHtmlFile(file),
            });
        } else {
            readHtmlFile(file);
        }
        return false; // chặn antd tự gửi lên server
    };

    const copyPageUrl = async () => {
        try {
            await navigator.clipboard.writeText(landingPageUrl(slug));
            message.success("Đã copy link trang");
        } catch {
            message.error("Trình duyệt chặn copy");
        }
    };

    if (loading) return <div className="p-4 text-content-3">Đang tải…</div>;

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <Button onClick={() => navigate("/page")}>
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Danh sách
                </Button>
                <div className="flex items-center gap-3">
                    <span className="text-sm">Hiển thị</span>
                    <Switch checked={isActive} onChange={setIsActive} />
                    <Button type="primary" loading={saving} onClick={onSave}>
                        {isEdit ? "Lưu thay đổi" : "Tạo trang"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* ----- Cột trái: thông tin + HTML ----- */}
                <div className="flex flex-col gap-3">
                    <div>
                        <label className="text-sm font-semibold">Tiêu đề</label>
                        <Input
                            value={title}
                            onChange={(e) => onTitleChange(e.target.value)}
                            placeholder="Mua hàng hoàn tiền là gì?"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-semibold">Đường dẫn (slug)</label>
                        <Input
                            value={slug}
                            addonBefore="/trang/"
                            onChange={(e) => {
                                slugTouched.current = true;
                                setSlug(toSlug(e.target.value));
                            }}
                            placeholder="huong-dan-hoan-tien"
                        />
                        {slug && (
                            <div className="mt-1 flex items-center gap-2 text-xs text-content-3">
                                <span className="truncate">{landingPageUrl(slug)}</span>
                                <Tooltip title="Copy link để dán vào Tiện ích App">
                                    <Button size="small" type="text" onClick={copyPageUrl}>
                                        <FontAwesomeIcon icon={faCopy} />
                                    </Button>
                                </Tooltip>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-semibold">Mô tả ngắn (tuỳ chọn)</label>
                        <Input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ghi chú nội bộ / meta description"
                        />
                    </div>

                    <AudienceSelect
                        value={audience}
                        days={audienceDays}
                        onChange={setAudience}
                        onDaysChange={setAudienceDays}
                        extra="Không đúng đối tượng thì mở link sẽ báo không tìm thấy trang. Lưu ý: mở trong app mà chưa đăng nhập trên web thì tính là khách vãng lai."
                    />

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-semibold">Nội dung HTML</label>
                            <div className="flex items-center gap-2">
                                <LandingPageDesignRules size="small" />
                                <Upload beforeUpload={onPickHtmlFile} showUploadList={false} accept=".html,.htm">
                                    <Button size="small">
                                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                                        Tải file HTML
                                    </Button>
                                </Upload>
                                <Upload customRequest={onUploadImage} showUploadList={false} accept="image/*">
                                    <Button size="small">
                                        <FontAwesomeIcon icon={faImage} className="mr-2" />
                                        Tải ảnh &amp; copy URL
                                    </Button>
                                </Upload>
                            </div>
                        </div>
                        <Input.TextArea
                            value={html}
                            onChange={(e) => setHtml(e.target.value)}
                            rows={22}
                            spellCheck={false}
                            style={{ fontFamily: "monospace", fontSize: 12 }}
                            placeholder="<!DOCTYPE html> … dán nguyên file HTML vào đây, hoặc bấm Tải file HTML"
                        />
                        <p className="text-xs text-content-3 mt-1">
                            Dán được cả file hoàn chỉnh (có &lt;style&gt; riêng), hoặc chọn thẳng file
                            .html trong máy. Trang hiển thị full màn hình, không có header/footer của web.
                        </p>
                    </div>

                    <LandingPageImages html={html} apiBase={API_PUBLIC_URL} />
                </div>

                {/* ----- Cột phải: xem thử ----- */}
                <LandingPagePreview html={html} />
            </div>
        </div>
    );
}

export default LandingPageForm;
